import { useMutation, useQueryClient } from 'react-query';
import { livresAPI } from '../services/api';
import { getLivreTagsWithColors } from '../utils/tagUtils';
import toast from 'react-hot-toast';

/**
 * Hook personnalisé pour gérer les mutations d'ajout et suppression de tags
 * @param {Object} livre - Le livre concerné (peut être null pour LivresPage)
 * @param {Array} tagsData - Liste des tags disponibles
 * @param {Function} getLivreById - Fonction pour récupérer un livre par ID (optionnel)
 * @param {Function} setForceUpdate - Fonction pour forcer le rechargement (optionnel)
 * @returns {Object} - Objet contenant les mutations et l'état de chargement
 */
export function useTagMutations(livre, tagsData, getLivreById = null, setForceUpdate = null) {
  const queryClient = useQueryClient();

  // Mutation pour ajouter un tag à un livre
  const addTagMutation = useMutation(
    ({ livreId, tagId }) => {
      // Récupérer les tags actuels du livre
      let currentTags;
      if (livre) {
        // Cas LivreDetailPage : livre est déjà chargé
        currentTags = getLivreTagsWithColors(livre);
      } else if (getLivreById) {
        // Cas LivresPage : récupérer le livre depuis la liste
        const livreFromList = getLivreById(livreId);
        currentTags = getLivreTagsWithColors(livreFromList);
      } else {
        throw new Error('Impossible de récupérer les tags du livre');
      }
      
      const existingTags = currentTags.filter(tag => !tag.id.toString().startsWith('temp-'));
      
      // Trouver le tag à ajouter dans la liste des tags disponibles
      const tagToAdd = tagsData?.find(tag => tag.id === tagId);
      if (!tagToAdd) {
        throw new Error('Tag non trouvé');
      }
      
      // Ajouter le nouveau tag au format attendu par updateLivre
      const newTags = [
        ...existingTags.map(tag => ({ id: tag.id, nom: tag.nom, isNew: false })),
        { id: tagToAdd.id, nom: tagToAdd.nom, isNew: false }
      ];
      
      // Créer un FormData et sérialiser les tags en JSON (comme dans ModifierLivrePage)
      const formData = new FormData();
      formData.append('tags', JSON.stringify(newTags));
      
      return livresAPI.update(livreId, formData);
    },
    {
      onSuccess: (response, { livreId, tagId }) => {
        toast.success('Tag ajouté avec succès');
        
        // Invalider les queries
        queryClient.invalidateQueries(['livre', livreId]);
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('tags-list');
        
        // Forcer le rechargement si setForceUpdate est fourni (cas LivreDetailPage)
        if (setForceUpdate) {
          setForceUpdate(prev => prev + 1);
        }
        
        console.log('✅ Tag ajouté au livre:', { livreId, tagId });
      },
      onError: (error) => {
        console.error('❌ Erreur ajout tag:', error);
        toast.error('Erreur lors de l\'ajout du tag');
      }
    }
  );

  // Mutation pour supprimer un tag d'un livre
  const removeTagMutation = useMutation(
    ({ livreId, tagId }) => {
      // Récupérer les tags actuels du livre
      let currentTags;
      if (livre) {
        // Cas LivreDetailPage : livre est déjà chargé
        currentTags = getLivreTagsWithColors(livre);
      } else if (getLivreById) {
        // Cas LivresPage : récupérer le livre depuis la liste
        const livreFromList = getLivreById(livreId);
        currentTags = getLivreTagsWithColors(livreFromList);
      } else {
        throw new Error('Impossible de récupérer les tags du livre');
      }
      console.log("removeTagMutation - currentTags:", currentTags)
      console.log("tagId:", tagId)
      const existingTags = currentTags.filter(tag => !tag.id.toString().startsWith('temp-'));
      
      // Supprimer le tag et formater pour updateLivre
      const newTags = existingTags
        .filter(tag => tag.id !== tagId)
        .map(tag => ({ id: tag.id, nom: tag.nom, isNew: false }));
      
      console.log("removeTagMutation - Tags actuels:", existingTags, "TagId à supprimer:", tagId, "Nouveaux tags:", newTags);
      
      // Créer un FormData et sérialiser les tags en JSON (comme dans ModifierLivrePage)
      // C'est nécessaire car axios avec multipart/form-data peut omettre les tableaux vides
      // si on passe directement un objet JavaScript
      const formData = new FormData();
      formData.append('tags', JSON.stringify(newTags));
      
      return livresAPI.update(livreId, formData);
    },
    {
      onSuccess: (response, { livreId, tagId }) => {
        toast.success('Tag supprimé avec succès');
        
        // Invalider les queries
        queryClient.invalidateQueries(['livre', livreId]);
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('tags-list');
        
        // Forcer le rechargement si setForceUpdate est fourni (cas LivreDetailPage)
        if (setForceUpdate) {
          setForceUpdate(prev => prev + 1);
        }
        
        console.log('✅ Tag supprimé du livre:', { livreId, tagId });
      },
      onError: (error) => {
        console.error('❌ Erreur suppression tag:', error);
        toast.error('Erreur lors de la suppression du tag');
      }
    }
  );

  return {
    addTagMutation,
    removeTagMutation,
    isTagLoading: addTagMutation.isLoading || removeTagMutation.isLoading
  };
}
